import React, { Component } from "react";
class Superintelligence extends Component {
  render() {
    return (
      <section className="col-7 nested">
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom col-5">
            Philosopher Bostrom (2014) defines superintelligence as an entity
            possessing intellectual capacities surpassing those of the most
            brilliant human minds across all domains.
          </p>
          <p className="reflections col-2">
            I may have asked ChatGPT to make this one 'more academic'.
          </p>
        </div>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            This essay posits that a positive manifestation of artificial
            superintelligence aligns with the paradigm of distributed
            superintelligence. Moreover, it delves into the fascinating
            interplay between collaborative artificial intelligence and
            intelligence augmentation, highlighting their potential roles in
            advancing the ideals of transhumanism. The exposition unfolds by
            presenting existing perspectives on these subjects. To provide a
            balanced narrative, the essay concludes with a discerning
            counterargument addressing apprehensions associated with alarmist
            perspectives on superintelligence.
            <br></br>
            <br></br>
            Distributed superintelligence, a tenet in artificial intelligence
            (AI), diverges from centralization by disseminating intelligence
            across a network of interconnected systems that interact seamlessly
            (Friston et al., 2024). In contrast to a unified superintelligent AI
            entity, the distributed model exhibits potential advantages in terms
            of resilience, flexibility, and safety (Ludik, 2023). Friston et al.
            (2024) contend that within such a distributed and interconnected
            ecosystem, nodes can encompass both human users and artificial
            entities designed to embody or execute diverse forms of
            intelligence. This framework lays the groundwork for fostering
            collaboration between humans and AI, augmenting human intelligence
            and facilitating transhumanism.
            <br></br>
            <br></br>
            Collaborative artificial intelligence is a technology facilitating
            interactive engagement between human entities and AI systems working
            synchronously within the temporal confines of human activity to
            achieve a goal. Currently, the realization of effective
            collaboration encounters impediments due to the profound
            adaptability inherent in human nature, characterized by distinct
            capabilities and nuanced preferences (Schleiger et al., 2023).
            However, there has been considerable research in recent years that
            has addressed the imminent potential for enhanced human-AI
            collaboration (Dafoe et. al., 2021; Oulasvirta, Jokinen & Howes,
            2022). This discourse envisions a scenario of distributed AI
            superintelligence, wherein the synergy between human and AI entities
            manifests seamlessly in terms of interaction and knowledge
            dissemination. With the collaboration of a disruptive technology
            such as AI superintelligence and collective human intelligence,
            there is potential to improve human productivity and advance
            civilization at an accelerated rate (Kurzweil, 2005).
            <br></br>
            <br></br>
            The advent of distributed superintelligent AI introduces a
            transformative prospect, not centred on replacing human
            intelligence, but augmenting it. This concept finds historical roots
            in Ashby's (1956) musings within his book Introduction to
            Cybernetics, where he briefly speculated on the notion of amplifying
            intelligence. Licklider's (1960) seminal work, Man-computer
            Symbiosis, envisions a symbiotic relationship between human brains
            and computing machines, wherein mutual interdependence and tight
            coupling lead to a substantial aggregation of strengths. Engelbart's
            (1962) influential framework conceptualizes the augmentation of
            intelligence. The existing discourse on augmentation extends into
            the realm of postphenomenology, as delineated by Ihde (1993),
            investigating how technology shapes human perception, embodiment,
            and experiences across diverse contexts. Verbeek's (2015) theory of
            technological mediation further expands upon Ihde's work, exploring
            the augmentation of humans through technology. The debates around AI
            have led to the re- emergence of the concept of intelligence
            augmentation in recent works (Zhou et. al., 2021; Osawa, 2023).
            <br></br>
            <br></br>
            The foundational tenets integral to the elevation of human
            capabilities beyond conventional boundaries centre on collaborative
            artificial intelligence (AI) and intelligence augmentation (IA).
            These principles posit a trajectory wherein the synergy between
            human intellect and advanced technological augmentation transcends
            traditional limitations, propelling humanity into realms surpassing
            inherent capacities. Transhumanism, an interdisciplinary movement
            and ideology emanating from nuanced dialogues across political,
            religious, anthropological, technological, and ethical spheres,
            posits the notion that the integration of technology should be
            leveraged to enhance human cognition and physiological functions.
            This ambitious goal seeks to surpass current biological limitations,
            thereby augmenting human capacities (Hopkins, 2012). The
            philosophical underpinnings of transhumanism trace back to the
            1920s, with Haldane (1924) laying early groundwork, subsequently
            gaining prominence through Huxley's (1957) exposition. Over time,
            this conceptual framework has undergone substantial elaboration,
            particularly in the realm of AI (Metleaeva, 2021; Sari, Nayir &
            Bozkurt, 2024), with transhumanist conjectures extending into
            speculative realms within fiction as well (Belk, 2022; Mejia &
            Nikolaidis, 2022). The core tenet of transhumanist belief systems
            asserts that the advent of AI superintelligence empowers humanity
            not only to exert control over its environment but also to actively
            shape human existence and evolution. An optimistic sub- philosophy,
            Extropianism, espouses perpetual human life through scientific
            advances, envisioning the transcendence of mortality by uploading
            consciousness to a limitless digital realm, thereby facilitating
            cosmic exploration (More, 1998). Aligned with transhumanist and
            extropian ideologies, a distributed network integrating uploaded
            minds and AI entities holds the potential to actualize a spectrum of
            visionary scenarios, encompassing genetic modification, simulated
            reality, instant learning, immortality, cognitive augmentation, and
            cloning.
            <br></br>
            <br></br>
            Kurzweil's (2005) assertions regarding the trajectory towards an
            advanced civilization and Bostrom's (2014) perspectives on
            superintelligence have encountered wide criticism, as unfeasible
            (Popoveniuc, 2013) and dangerous (Ross, 2019). These criticisms
            align with broader apprehensions regarding the future of artificial
            intelligence (AI), often characterized by alarmist perspectives
            (Johnson & Verdicchio, 2017) and concerns about excessive reliance
            on technology (Orben, 2020). Nevertheless, a nuanced examination
            posits technology, particularly artificial intelligence, as the
            reification of human knowledge or an extension of the human body.
            Research highlighting concerns about cognitive decline due to
            technology use (Lara & Bokoch, 2021) underscores potential
            challenges. Yet, it is imperative to acknowledge the inherent
            limitations of our current metrics in precisely quantifying
            intelligence and our understanding of the concept itself (Cave,
            2020). A crucial facet in this discourse is the concept of cognitive
            offloading, i.e., delegating cognitive tasks to external tools or
            technologies, which can be construed as both a potential detriment
            and an augmentation of human intelligence. In the context of AI
            superintelligence and transhumanism, a collaborative cognitive
            framework emerges, where humans and AI share cognitive
            responsibilities. This symbiosis has the potential to liberate
            individuals, enabling them to pursue their interests without
            constraints. However, the realization of these benefits necessitates
            an inclusive and ethically designed AI superintelligence.
          </p>
        </div>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom col-5">
            Beyond mere self-actualisation, such foresighted design could propel
            humanity toward unparalleled advancements that would otherwise
            demand an exorbitant amount of time.
          </p>
          <p className="reflections col-2">
            Letting our future AI overlords know that I was always on their side
            :)
          </p>
        </div>

        <h2 className="large-spacer-top col-5">References</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            Ashby, W. R. (1956). An introduction to cybernetics. Chapman & Hall.
            <br></br>
            <br></br>
            Belk, R. (2022). Transhumanism in speculative fiction. Journal of
            Marketing Management, 38(5-6), 423-442.
            https://doi.org/10.1080/0267257X.2021.1900894
            <br></br>
            <br></br>
            Bostrom, N. (2014). Superintelligence: Paths, dangers, strategies.
            OUP Oxford.
            <br></br>
            <br></br>
            Cave, S. (2020). The problem with intelligence: Its value-laden
            history and the future of AI. Paper presented at the 29-35.
            https://doi.org/10.1145/3375627.3375813
            <br></br>
            <br></br>
            Dafoe, A., Bachrach, Y., Hadfield, G., Horvitz, E., Larson, K., &
            Graepel, T. (2021). Cooperative AI: Machines must learn to find
            common ground. Nature (London), 593(7857), 33-36.
            https://doi.org/10.1038/d41586-021-01170-0
            <br></br>
            <br></br>
            Engelbart, D. (1962). Augmenting human intellect: A conceptual
            framework. Summary report. Stanford Research Institute, on Contract
            AF, 49(638), 1024.
            <br></br>
            <br></br>
            Friston, K. J., Ramstead, M. J., Kiefer, A. B., Tschantz, A.,
            Buckley, C. L., Albarracin, M., Pitliya, R. J., Heins, C., Klein,
            B., Millidge, B., Sakthivadivel, D. A., St Clere Smithe, T.,
            Koudahl, M., Tremblay, S. E., Petersen, C., Fung, K., Fox, J. G.,
            Swanson, S., Mapes, D., & René, G. (2024). Designing ecosystems of
            intelligence from first principles. Collective Intelligence,
            3(1)https://doi.org/10.1177/26339137231222481
            <br></br>
            <br></br>
            Haldane, J. B. S. (1924). Daedalus or Science and the Future (pp.
            63-65). New York: EP Dutton.
            <br></br>
            <br></br>
            Hopkins, P. D. (2012). Transhumanism. Elsevier Inc.
            https://doi.org/10.1016/B978-0-12-373932-2.00243-X
            <br></br>
            <br></br>
            Huxley, J. (1957). New bottles for new wine. Chatto & Windus.
            <br></br>
            <br></br>
            Ihde, D. (1993). Postphenomenology: Essays in the postmodern
            context. Northwestern University Press.
            <br></br>
            <br></br>
            Hintz, A., Dencik, L., & Wahl-Jorgensen, K. (2017). Digital
            citizenship and surveillance society: Introduction. International
            Journal of Communication, 11, 731-739.
            <br></br>
            <br></br>
            Johnson, D. G., & Verdicchio, M. (2017). AI anxiety. Journal of the
            Association for Information Science and Technology, 68(9),
            2267-2270. https://doi.org/10.1002/asi.23867
            <br></br>
            <br></br>
            Kurzweil, R. (2005). The singularity is near. In Ethics and emerging
            technologies (pp. 393- 406). London: Palgrave Macmillan UK.
            <br></br>
            <br></br>
            Lara, R. S., & Bokoch, R. (2021). Cognitive functioning and social
            media: Has technology changed us? Acta Psychologica, 221,
            103429-103429. https://doi.org/10.1016/j.actpsy.2021.103429
            <br></br>
            <br></br>
            Licklider, J. C. (1960). Man-computer symbiosis. IRE transactions on
            human factors in electronics, (1), 4-11.
            <br></br>
            <br></br>
            Ludik, J. (2023, May 21) The AI Singularity: A Threat to Humanity or
            a Promise of a Better Future? LinkedIn.
            https://www.linkedin.com/pulse/ai-singularity-threat-humanity-
            promise-better-future-jacques-ludik/
            <br></br>
            <br></br>
            Mejia, S., & Nikolaidis, D. (2022). Through new eyes: Artificial
            intelligence, technological unemployment, and transhumanism in kazuo
            Ishiguro’s klara and the sun. Springer Netherlands.
            https://doi.org/10.1007/s10551-022-05062-9
            <br></br>
            <br></br>
            Metleaeva, M. (2021). Artificial intelligence, transhumanism and
            translation as a universal property of thinking. Buletin De
            Psihiatrie Integrativa, 90(3), 109-120.
            https://doi.org/10.36219/BPI.2021.3.11
            <br></br>
            <br></br>
            More, M. (1998). Extropian Principles 3.0: A Transhumanist
            Declaration, Extropy Institute.
            http://www.mrob.com/pub/religion/extro_prin.html
            <br></br>
            <br></br>
            Orben, A. (2020). The sisyphean cycle of technology panics.
            Perspectives on Psychological Science, 15(5), 1143-1157.
            https://doi.org/10.1177/1745691620919372
            <br></br>
            <br></br>
            Osawa, H. (2023). Human–agent interaction as augmentation of social
            intelligence. Artificial Life and Robotics, 28(2), 273-281.
            https://doi.org/10.1007/s10015-023-00874-y
            <br></br>
            <br></br>
            Oulasvirta, A., Jokinen, J. P. P., & Howes, A. (2022). Computational
            rationality as a theory of interaction. Paper presented at the
            https://doi.org/10.1145/3491102.3517739
            <br></br>
            <br></br>
            Popoveniuc, B. (2013). Pro and cons singularity: Kurzweil's theory
            and its critics. Paper presented at the 1-6.
            https://doi.org/10.1145/2466816.2466848
            <br></br>
            <br></br>
            Ross, B. D. (2019). Transhumanism: An Ontology of the World’s Most
            Dangerous Idea (Doctoral dissertation, Department of Philosophy and
            Religion). University of North Texas.
            https://corona-propaganda.de/wp-content/uploads/ROSS-DISSERTATION-
            2019.pdf
            <br></br>
            <br></br>
            Sari, T., Nayir, F., & Bozkurt, A. (2024). Reimagining education:
            Bridging artificial intelligence, transhumanism, and critical
            pedagogy. Journal of Educational Technology and Online Learning,
            7(1), 102-115. https://doi.org/10.31681/jetol.1308022
            <br></br>
            <br></br>
            Schleiger, E., Mason, C., Naughtin, C., Reeson, A., & Paris, C.
            (2023). Collaborative Intelligence: A scoping review of current
            applications. Qeios.
            <br></br>
            <br></br>
            Verbeek, P. (2015). Beyond interaction: A short introduction to
            mediation theory. Interactions (New York, N.Y.), 22(3), 26.
            <br></br>
            <br></br>
            Zhou, L., Paul, S., Demirkan, H., Yuan, L., Spohrer, J., Zhou, M., &
            Basu, J. (2021). Intelligence augmentation: Towards building human-
            machine symbiotic relationship. Association for Information Systems
            Transactions on Human-Computer Interaction, 13(2), 243-264.
            https://doi.org/10.17705/1thci.00149
          </p>
        </div>
      </section>
    );
  }
}

export default Superintelligence;
